// src/pages/dashboard/components/OrderStatusDistribution.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { PieChart } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { formatCurrency } from '../../../utils/currencyUtil';
import { getOrderStatusDistribution } from '../../../services/api/orders';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import DateRangePresetSelect, { getDateRangeByPreset } from './DateRangePresetSelect';
import selectStyles from './DateRangePresetSelect.module.css';
import styles from './OrderStatusDistribution.module.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const cssVar = (name, fallback) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

const centerTextPlugin = {
  id: 'centerText',
  afterDraw: (chart, args, opts) => {
    if (!opts?.display || !chart.chartArea) return;
    const { ctx, chartArea: { left, right, top, bottom } } = chart;
    const x = (left + right) / 2;
    const y = (top + bottom) / 2;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = cssVar('--color-text-primary', '#1f2937');
    ctx.font = `700 24px ${cssVar('--font-family', 'sans-serif')}`;
    ctx.fillText(opts.value, x, y - 9);
    ctx.fillStyle = cssVar('--color-text-secondary', '#6b7280');
    ctx.font = `500 12px ${cssVar('--font-family', 'sans-serif')}`;
    ctx.fillText(opts.label, x, y + 14);
    ctx.restore();
  }
};

const OrderStatusDistribution = ({ shopId, currency }) => {
  const { t } = useLocalization();
  const [datePreset, setDatePreset] = useState('thisMonth');
  const dateRange = useMemo(() => getDateRangeByPreset(datePreset), [datePreset]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartView, setChartView] = useState('count'); // 'count', 'value', 'bars'

  useEffect(() => {
    fetchStatusDistribution();
  }, [shopId, dateRange]);

  const fetchStatusDistribution = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (shopId && shopId !== 'all') {
        params.shopId = shopId;
      }
      if (dateRange?.from) {
        params.dateFrom = dateRange.from;
      }
      if (dateRange?.to) {
        params.dateTo = dateRange.to;
      }
      const response = await getOrderStatusDistribution(params);
      setData(response);
    } catch (err) {
      console.error('Error fetching status distribution:', err);
      setError(err.message || t('dashboard.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const calculatePercentage = (value, total) => {
    if (!total || total === 0) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  const renderChart = () => {
    if (!data || !data.distribution || data.distribution.length === 0) {
      return (
        <div className={styles.emptyState}>
          <p>{t('dashboard.noData')}</p>
        </div>
      );
    }

    const { distribution, totals } = data;
    const filteredDistribution = distribution.filter(item => item.orderCount > 0);

    if (chartView === 'bars') {
      return (
        <div className={styles.barsView}>
          {filteredDistribution.map((item, index) => {
            const percentage = calculatePercentage(item.orderCount, totals.totalOrders);
            
            return (
              <div key={index} className={styles.statusItem}>
                <div className={styles.statusHeader}>
                  <div className={styles.statusInfo}>
                    <div 
                      className={styles.statusDot} 
                      style={{ backgroundColor: item.colorCode || '#94a3b8' }}
                    ></div>
                    <span className={styles.statusLabel}>{item.statusLabel}</span>
                  </div>
                  <span className={styles.percentage}>{percentage}%</span>
                </div>
                
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill}
                    style={{ 
                      width: `${percentage}%`,
                      backgroundColor: item.colorCode || '#94a3b8'
                    }}
                  ></div>
                </div>
                
                <div className={styles.statusStats}>
                  <span className={styles.statValue}>
                    {formatNumber(item.orderCount)} {t('dashboard.orders')}
                  </span>
                  <span className={styles.statValue}>
                    {formatCurrency(item.totalValue, currency)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Doughnut chart for count or value
    const chartData = {
      labels: filteredDistribution.map(item => item.statusLabel),
      datasets: [
        {
          data: chartView === 'count' 
            ? filteredDistribution.map(item => item.orderCount)
            : filteredDistribution.map(item => item.totalValue),
          backgroundColor: filteredDistribution.map(item => item.colorCode || '#94a3b8'),
          borderColor: cssVar('--color-bg-primary', '#ffffff'),
          borderWidth: 2,
          hoverOffset: 10
        }
      ]
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 15,
            font: {
              size: 12
            },
            generateLabels: (chart) => {
              const data = chart.data;
              if (data.labels.length && data.datasets.length) {
                return data.labels.map((label, i) => {
                  const value = data.datasets[0].data[i];
                  const formattedValue = chartView === 'count' 
                    ? formatNumber(value) 
                    : formatCurrency(value, currency);
                  return {
                    text: `${label}: ${formattedValue}`,
                    fillStyle: data.datasets[0].backgroundColor[i],
                    hidden: false,
                    index: i
                  };
                });
              }
              return [];
            }
          }
        },
        centerText: {
          display: chartView === 'count',
          value: formatNumber(totals.totalOrders),
          label: t('dashboard.totalOrders')
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || '';
              const value = context.parsed;
              const formattedValue = chartView === 'count' 
                ? formatNumber(value) 
                : formatCurrency(value, currency);
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              return `${label}: ${formattedValue} (${percentage}%)`;
            }
          }
        }
      },
      cutout: '65%'
    };

    return (
      <>
        <div className={styles.chartWrapper}>
          <Doughnut data={chartData} options={chartOptions} plugins={[centerTextPlugin]} />
        </div>
        {chartView === 'value' && (
          <div className={styles.valueSummary}>
            <div className={styles.centerValue}>{formatCurrency(totals.totalValue, currency)}</div>
            <div className={styles.centerLabel}>{t('dashboard.totalOrderValue')}</div>
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.ordersByStatus')}</h3>
          <div className={selectStyles.headerActions}>
            <DateRangePresetSelect value={datePreset} onChange={setDatePreset} />
            <PieChart size={20} />
          </div>
        </div>
        <div className={styles.skeleton}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={styles.skeletonItem}>
              <div className={styles.skeletonBar}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.ordersByStatus')}</h3>
          <div className={selectStyles.headerActions}>
            <DateRangePresetSelect value={datePreset} onChange={setDatePreset} />
            <PieChart size={20} />
          </div>
        </div>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={fetchStatusDistribution} className={styles.retryButton}>
            {t('dashboard.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!data || !data.distribution || data.distribution.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.ordersByStatus')}</h3>
          <div className={selectStyles.headerActions}>
            <DateRangePresetSelect value={datePreset} onChange={setDatePreset} />
            <PieChart size={20} />
          </div>
        </div>
        <div className={styles.emptyState}>
          <p>{t('dashboard.noData')}</p>
        </div>
      </div>
    );
  }

  const { totals } = data;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h3>{t('dashboard.ordersByStatus')}</h3>
          <p className={styles.subtitle}>
            {formatNumber(totals.totalOrders)} {t('dashboard.orders')} • {formatCurrency(totals.totalValue, currency)}
          </p>
        </div>
        <div className={selectStyles.headerActions}>
          <DateRangePresetSelect value={datePreset} onChange={setDatePreset} />
          <PieChart size={20} />
        </div>
      </div>

      <div className={styles.viewToggle}>
        <button
          className={`${styles.toggleButton} ${chartView === 'count' ? styles.active : ''}`}
          onClick={() => setChartView('count')}
          title={t('dashboard.orderCount')}
        >
          {t('dashboard.orderCount')}
        </button>
        <button
          className={`${styles.toggleButton} ${chartView === 'value' ? styles.active : ''}`}
          onClick={() => setChartView('value')}
          title={t('dashboard.orderValue')}
        >
          {t('dashboard.orderValue')}
        </button>
        <button
          className={`${styles.toggleButton} ${chartView === 'bars' ? styles.active : ''}`}
          onClick={() => setChartView('bars')}
          title={t('dashboard.barChart')}
        >
          {t('dashboard.barChart')}
        </button>
      </div>

      <div className={styles.content}>
        {renderChart()}
      </div>
    </div>
  );
};

export default OrderStatusDistribution;